import React from 'react';

/* Shared keystone descriptor — single source of truth for the visual
   identity of Vancomycin / Adalimumab / Metformin gold cards.
   Mirrors backend services/keystone_engine/registry.py::_ACCENT_RGB.
   Consumed by KeystonePickModal (pick screen) and the player config
   surfaces (PlayerEquipment, PlayerShop) so the keystone gold cards
   look identical wherever they appear. */
export const KEYSTONES = [
  {
    id: 'vancomycin',
    // Fantasy-frame ability name (parity with Metformin's "Guideline Oracle").
    // The old "Precise Dosing" was a mechanic label, not an identity; it now
    // rides the ledger CHIP below. Shown as the tooltip header + on pick/equip/shop.
    abilityName: 'Pharmacokinetic Precision',
    genericName: 'Vancomycin',
    drugClass: 'Glycopeptide antibiotic',
    accent: '#ef4444',
    ringRgb: '239, 68, 68',
    // Pick-screen category. Framed around DOSING, not "crit" (Edward 2026-06-27):
    // new users may not know what a crit is, so the first touchpoint canonicalizes
    // the keystone through dosing within the game's context.
    infoHeader: 'Dosing Mastery',
    infoBody: 'Pick if you struggle with calculations.',
    // BROAD / DURABLE copy (Edward 2026-06-27): the Vancomycin beat MECHANIC is
    // still being authored, so the tooltip copy stays at the VIBE level so it
    // remains true across mechanic updates. The vibe: the beats sharpen your math
    // intuition and lock the dosing formulas into memory. No mechanic specifics
    // (no "crit"/"miss"/"batch"/"dosing calculation"), ONE sentence (Edward:
    // dislikes the second-sentence prose). Do NOT re-add mechanic claims here
    // until the beat mechanic is final.
    description: 'These keystone beats sharpen your math intuition and drill the key dosing formulas into memory.',
    pros: ['Sharpens dosing math', 'Drills key formulas'],
    cons: [],
    // Single-section keystone: Vancomycin reads as one broad effect, so its tooltip
    // is one section (not the forced two). The section eyebrow uses the canonical
    // "How the beats work" label (parity with Metformin).
    singleSection: true,
    beatsLabel: 'How the beats work',
    beatsBody: 'These keystone beats sharpen your math intuition and drill the key dosing formulas into memory.',
    // ONE canonical chip — tone 'good' carries Vancomycin's own red accent. No
    // number revealed on hover (no `detail`) because the magnitude belongs to the
    // still-fluid mechanic; and NO "Miss" chip (there is no real miss cost to show).
    beatLedger: [
      // signature: render as a faithful replica of the in-combat result pill
      // (full width, label left, value right). No value yet (fluid mechanic).
      { tone: 'good', word: 'Precise Dosing', accent: '239, 68, 68', signature: true },
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 5h14M5 5l7 7-7 7M5 19h14" />
      </svg>
    ),
  },
  {
    id: 'metformin',
    abilityName: 'Guideline Oracle',
    genericName: 'Metformin',
    drugClass: 'Biguanide antihyperglycemic',
    accent: '#34d399',
    ringRgb: '52, 211, 153',
    infoHeader: 'Resource Management',
    infoBody: 'Pick if you struggle with guidelines.',
    // Accurate to the engine (verified 2026-06-26): a correct guideline_mcq
    // keystone beat deals NO boss damage and builds NO lactic; it lands a flat
    // +10 HP "Good Outcome" heal (batch_combat_engine good_outcome, uncapped).
    // Lactic is built ONLY by the optional green Heal action (combat_heal_action:
    // drill a card, +1 lactic per submit, +5 HP base on correct). Lactic then
    // softens your outgoing damage and raises incoming.
    description: 'Correct guideline beats land a Good Outcome: a flat +10 HP heal. Tap Heal anytime to drill a card for more HP, but each use stacks lactic, which softens your hits and raises the damage you take.',
    // Two-section keystone tooltip (Edward 2026-06-26): TOP = how the beats work
    // (the +10 Good Outcome), BOTTOM = the Heal action (the lactic lever, where
    // the green/red pro/con tags live). Keep the word "lactic" verbatim so
    // renderStatusText paints the lactic status glyph inside the tooltip.
    beatsBody: 'Each beat is a guideline call. Answer correctly for a Good Outcome: a flat +10 HP heal, no lactic and no risk.',
    effectsBody: 'Tap Heal anytime to drill a card for HP. Every use adds a lactic stack, and as lactic climbs your hits soften while you take more.',
    // The green/red chips are the MECHANIC LEDGER (Edward 2026-06-26): the exact
    // numbers, not vibes. Every value below is verified against the combat engine
    // (adversarial workflow wf_537bc582). Beat chips ride Section 1 (the +10 Good
    // Outcome), heal/lactic chips ride Section 2 (the Heal action lever).
    beatGains: ['Correct: +10 HP'],
    beatCosts: ['No boss damage'],
    pros: ['Correct drill: +5 HP base'],
    cons: [
      '+1 lactic each use (any answer)',
      'Heal weakens 10% per lactic stack',
      'Each lactic: -8% your hits, +12% taken',
    ],
    // CONSOLIDATED LEDGER (Edward 2026-06-26): each section's bottom green/red split
    // collapses to exactly the canonical chips — glyph + resource word by default,
    // hover reveals ONLY the number+magnitude. Engine-verified (workflow wf_537bc582):
    //   beat  -> one green "Good Outcome" (+10 HP flat heal, no cost).
    //   heal  -> green "Heal" (+5 HP base on a correct drill) + red "Lactic" (+1 per
    //            use; its -8%/+12% damage swing is what lactic DOES, folded in).
    beatLedger: [
      // tone 'good' = metformin-teal, identical to the animation's Good Outcome pill.
      // signature: faithful replica of the in-combat Good Outcome result pill
      // (full width, label left, "+10 HP" value inline on the right).
      { tone: 'good', word: 'Good Outcome', detail: '+10 HP', signature: true },
    ],
    healLedger: [
      { tone: 'pos', word: 'Heal', detail: '+5 HP' },
      // Hover reveals lactic's EFFECT (the per-stack damage swing), NOT a redundant
      // "+1 lactic" — the face already names the resource. Engine-verified: -8%
      // outgoing (floor 50%) / +12% incoming per stack.
      { tone: 'neg', word: 'Lactic', detail: '-8% your hits, +12% taken' },
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="5" r="2" />
        <circle cx="6" cy="19" r="2" />
        <circle cx="18" cy="19" r="2" />
        <path d="M12 7v4M12 11l-6 6M12 11l6 6" />
      </svg>
    ),
  },
  {
    id: 'adalimumab',
    abilityName: 'Cytokine Lockdown',
    genericName: 'Adalimumab',
    drugClass: 'Anti-TNF-α monoclonal antibody',
    accent: '#60a5fa',
    ringRgb: '96, 165, 250',
    infoHeader: 'Inflammatory Blockade',
    infoBody: 'Pick if you struggle with drug information.',
    // Accurate to DupilumabStrategy (SATA pass/fail beats; dodge tokens block
    // incoming hits; a miss deals no boss damage AND no self-damage).
    description: 'Your keystone beats are select-all panels. Build dodge tokens to block incoming hits; a miss never punishes you, but only a full answer counts.',
    pros: ['Misses never self-damage', 'Dodge tokens block hits', 'Rewards drug-info recall'],
    cons: ['No partial SATA credit', 'Full answer or nothing'],
    // Two-section keystone (Edward 2026-06-26 canon): the Lockdown ability EARNS
    // dodge tokens, then a token is SPENT to block a hit. Engine truth (DupilumabStrategy
    // + batch_combat_engine.py:6542-6548, parity workflow wf_c5e013a6): a fully-correct
    // SATA beat grants +1 dodge token (no cap); a miss deals 0 boss damage AND 0
    // self-damage but resets the streak; a token absorbs one full round of incoming
    // boss damage. tone 'good' carries Adalimumab's own blue accent.
    beatsLabel: 'The Lockdown ability',
    beatsBody: 'Each beat is a select-all panel. Answer every option correctly to earn a dodge token; a miss never punishes you, but only a full answer counts.',
    effectsLabel: 'Dodge tokens',
    effectsBody: 'Spend a dodge token to block an incoming boss attack for a full round. Unspent tokens carry between rounds.',
    beatLedger: [
      { tone: 'good', word: 'Dodge', detail: '+1 token per correct SATA', accent: '96, 165, 250' },
      { tone: 'neg', word: 'Miss', detail: '0 damage, 0 self-damage, streak reset' },
    ],
    healLedger: [
      { tone: 'good', word: 'Block', detail: '1 token blocks a full round of boss damage', accent: '96, 165, 250' },
      { tone: 'neg', word: 'Spent', detail: '-1 token per block' },
    ],
    icon: (
      // Union (∪) — Adalimumab's question type is SATA ("Select All That
      // Apply"); the answer is the UNION of every applicable option.
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 5L6 12A6 6 0 0 0 18 12L18 5" />
      </svg>
    ),
  },
];

/* Just the SVG path `d` strings for each keystone, in the same 24x24
   viewBox as the icon component. Used where consumers can only render
   raw path data (e.g. ProgressMap's type='keystone' SVG node, which
   embeds the path directly inside the atom circle). Keep in sync with
   the icon JSX above — both reference the same shape. */
const KEYSTONE_ICON_PATHS = {
  vancomycin: 'M5 5h14M5 5l7 7-7 7M5 19h14',
  metformin: 'M12 7v4M12 11l-6 6M12 11l6 6',
  adalimumab: 'M6 5L6 12A6 6 0 0 0 18 12L18 5',
};

export function getKeystoneIconPath(idOrGeneric) {
  if (!idOrGeneric) return null;
  const k = getKeystoneDescriptor(idOrGeneric);
  return k ? KEYSTONE_ICON_PATHS[k.id] : null;
}

/* Effect-communicating glyphs for the non-keystone drug cards on the skill
   tree (card_unlock nodes). Same 24x24 viewBox + stroke idiom as the keystone
   paths above; each is an array of `d` strings (multi-stroke glyphs). The
   shape encodes the card's EFFECT — color is supplied by the node's
   keystone-affinity logic in skillTreeLayout.js, NOT here. Keyed by
   drug_slug (lowercase). */
export const DRUG_ICON_PATHS = {
  // Vancomycin family — gauge+needle (narrow therapeutic index)
  digoxin: ['M4 16a8 8 0 0 1 16 0', 'M12 16l3.6-5', 'M11 6.2h2'],
  // Vancomycin family — converging arrows into one strike (synergistic strike)
  rifampin: ['M5 5l4.5 4.5', 'M19 5l-4.5 4.5', 'M12 9.5V20', 'M8.5 16.5l3.5 3.5 3.5-3.5'],
  // Vancomycin family — rising stacks + up arrow (zero-order ramp)
  phenytoin: ['M6 20v-3', 'M11 20v-7', 'M16 20v-11', 'M13.4 6.6L16 4l2.6 2.6'],
  // Metformin family — shield + buffer wave (buffered recovery)
  sodium_bicarbonate: ['M12 3l7 2.4v5.4c0 4.2-3 7.4-7 8.7-4-1.3-7-4.5-7-8.7V5.4z', 'M8 11.4c1-1.5 2-1.5 3 0s2 1.5 3 0'],
  // Adalimumab family — breath gust (second wind / quick relief)
  albuterol: ['M3 9h10.5a2.5 2.5 0 1 0-2.5-2.5', 'M3 13h13a2.5 2.5 0 1 1-2.5 2.5', 'M3 17h7'],
  // Adalimumab augment — lightning bolt (stimulant / Hyperfocus). Line-art to
  // match the reference set's thin-stroke weight (was an oversized recraft fill).
  methylphenidate: ['M13 2.5 L6.5 13.5 H11 L10.5 21.5 L17.5 10.5 H13 Z'],
  // Adalimumab augment — heart + slow-rate dash (beta-blocker / Steady Nerves).
  propranolol: ['M12 20.5C5 16 3.5 11.5 3.5 8.5A4 4 0 0 1 12 6 4 4 0 0 1 20.5 8.5C20.5 11.5 19 16 12 20.5Z', 'M8.5 10.5H15.5'],
  // Metformin augment — kidney + glucosuria droplet (SGLT2i / Ketone Charges).
  empagliflozin: ['M9.5 4C5.4 4 3 7 3 11.5S5.4 19 9.5 19c2 0 3.2-1.2 3.2-2.9 0-2.4-2.4-2.6-2.4-4.6s2.4-2.2 2.4-4.6C12.7 5.2 11.5 4 9.5 4z', 'M16.5 13c1.6 2 2.6 3.4 2.6 4.7a2.6 2.6 0 0 1-5.2 0C13.9 16.4 14.9 15 16.5 13z'],
  // Metformin augment — up-arrow from baseline (mobilize reserves / Glycogen Reserve).
  glucagon: ['M12 20.5V8', 'M7.5 12L12 7.5L16.5 12', 'M6 20.5h12'],
  // Warfarin hub — blood droplet (anticoagulant / bleed / lifesteal)
  warfarin: ['M12 3c3.2 4.6 5.5 7.7 5.5 10.6a5.5 5.5 0 0 1-11 0C6.5 10.7 8.8 7.6 12 3z', 'M9.8 13.2a2.2 2.2 0 0 0 4.4 0'],
  // card_unlock default — first-aid cross (folate rescue / antidote)
  folic_acid: ['M10 4h4v6h6v4h-6v6h-4v-6H4v-4h6z'],
  // ── Remaining gold-card line-art (2026-06-04) — redrawn from oversized recraft
  //    fills to 24-unit thin-stroke icons so every tree node matches the
  //    reference set's weight. Recraft fills stay in drugIcons.js for HUD pills.
  acetaminophen: ['M11 6a2 2 0 0 1 4 0v7.5a3.5 3.5 0 1 1-4 0z', 'M13 9v4.5'], // thermometer (antipyretic)
  amphetamine: ['M12 3v6', 'M12 15v6', 'M3 12h6', 'M15 12h6', 'M6.5 6.5l3 3', 'M17.5 6.5l-3 3'], // stimulant spark
  caffeine: ['M5 9h11v3a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4z', 'M16 10h2a2 2 0 0 1 0 4h-1.5', 'M8.5 3c-.6 1 .6 1.6 0 2.6', 'M11.5 3c-.6 1 .6 1.6 0 2.6'], // coffee cup + steam
  cyclophosphamide: ['M8 4c0 4 8 4 8 8s-8 4-8 8', 'M16 4c0 4-8 4-8 8s8 4 8 8', 'M9.2 8h5.6', 'M9.2 16h5.6'], // DNA helix (alkylating)
  donepezil: ['M9 5a3 3 0 0 0-3 3 3 3 0 0 0-1.2 5.2A3 3 0 0 0 8 18a3 3 0 0 0 8 0 3 3 0 0 0 3.2-4.8A3 3 0 0 0 15 5a3 3 0 0 0-6 0z', 'M12 5.5v13'], // brain (cognition)
  dronabinol: ['M12 21v-7', 'M12 15c-2.6-.6-4.8-3.2-5.3-7 3.2.4 5.3 2.4 5.3 5.2', 'M12 15c2.6-.6 4.8-3.2 5.3-7-3.2.4-5.3 2.4-5.3 5.2', 'M12 11.5c-1.8-.6-3.3-2.4-3.6-4.8 2.2.4 3.6 1.8 3.6 3.8', 'M12 11.5c1.8-.6 3.3-2.4 3.6-4.8-2.2.4-3.6 1.8-3.6 3.8'], // cannabis leaf
  gabapentin: ['M3 12h3l2-5 3 10 2.5-6 1.5 3h6'], // damping nerve wave (neuropathic)
  hydroxyzine: ['M17 4a8 8 0 1 0 3.5 11A6.5 6.5 0 0 1 17 4z'], // crescent moon (sedating antihistamine)
  ibuprofen: ['M13 3c2.8 3.8 4.5 5.8 4.5 8.8a5.5 5.5 0 0 1-11 0c0-1.6.8-3 2-4.2.4 1.3 1.2 1.9 2.2 2.1 0-2.6-1-4.2.3-6.7z', 'M5 19L19 6'], // flame + slash (anti-inflammatory)
  insulin_glargine: ['M5 19l9-9', 'M12 5l7 7', 'M14.5 7.5l-2 2', 'M4 22h16'], // syringe + basal flat line (long-acting)
  insulin_lispro: ['M5 19l9-9', 'M12 5l7 7', 'M14.5 7.5l-2 2', 'M5 22l3.5-3.5L12 22'], // syringe + spike (rapid)
  levodopa: ['M8 8l4-2 4 2v4l-4 2-4-2z', 'M16 10.5l3-1.2', 'M8 13.5l-2.2 3'], // catecholamine ring (dopamine)
  mannitol: ['M12 3c2 3 3.3 4.6 3.3 6.3a3.3 3.3 0 0 1-6.6 0C8.7 7.6 10 6 12 3z', 'M6 13c1.2 1.8 2 2.8 2 3.8a2 2 0 0 1-4 0c0-1 .8-2 2-3.8z', 'M18 13c1.2 1.8 2 2.8 2 3.8a2 2 0 0 1-4 0c0-1 .8-2 2-3.8z'], // osmotic diuresis droplets
  memantine: ['M12 5a7 7 0 1 0 0 14 7 7 0 1 0 0-14z', 'M7.2 12h9.6'], // blocked receptor (NMDA antagonist)
  methotrexate: ['M10 4h4v6h6v4h-6v6h-4v-6H4v-4h6z', 'M5 20L20 5'], // anti-folate cross + slash
  methylene_blue: ['M9.5 3h5', 'M10.5 3v5l-3.8 8.5a2 2 0 0 0 1.8 2.9h7a2 2 0 0 0 1.8-2.9L14.5 8V3', 'M8 14h8'], // vial/flask (dye antidote)
  naloxone: ['M6 12a6 6 0 1 1 1.8 4.3', 'M4.5 12.5L6 16l3.5-1.2'], // reversal circular arrow (antagonist)
  sertraline: ['M6 14c2 3 10 3 12 0', 'M12 4v6', 'M9 7l3-3 3 3'], // mood-up (SSRI)
  testosterone: ['M9.5 14.5a4.5 4.5 0 1 0 9 0 4.5 4.5 0 1 0-9 0', 'M13.2 11.3L19 5.5', 'M14.5 5H19.5v4.5'], // Mars symbol (androgen)
  // ── HTN skill-tree region drug glyphs (2026-07-02) — the antihypertensive
  //    cards live IN the skill tree, so they get thin-stroke 24x24 line-art like
  //    every other tree node (NOT the filled recraft silhouettes in drugIcons.js —
  //    those are the drug-map/HUD-pill entity, a SEPARATE thing). Without a
  //    line-art entry here, skillTreeLayout's icon gate falls back to the filled
  //    recraft glyph = wrong family (Edward 2026-07-02: "svgs not from the same
  //    family"). Class-coherent motifs; color comes from the node's per-class
  //    accent in computeHtnRegionLayout, NOT here.
  // ACE inhibitors — blocked conversion (arrow into a stop bar; ACE "converts", inhibitor stops it)
  lisinopril: ['M4 12h9', 'M10.5 9l3 3-3 3', 'M16.5 7.5v9'],
  enalapril: ['M4 12h9', 'M10.5 9l3 3-3 3', 'M16.5 7.5v9', 'M19 9.5l1.6 1.6-1.6 1.6'],
  benazepril: ['M4 12h9', 'M10.5 9l3 3-3 3', 'M16.5 7.5v9', 'M19.2 12h1.6'],
  // ARBs — ligand plugged into the AT1 receptor pocket (receptor blockade)
  losartan: ['M7 8v3a5 5 0 0 0 10 0V8', 'M12 3.5v6', 'M9.5 5.5h5'],
  olmesartan: ['M7 8v3a5 5 0 0 0 10 0V8', 'M12 3.5v6', 'M9.5 5.5h5', 'M10 12.8h4'],
  valsartan: ['M7 8v3a5 5 0 0 0 10 0V8', 'M12 3.5v6', 'M9.5 5.5h5', 'M12 12.5v3'],
  // K-sparing / MRA — shield guarding a K (spares potassium)
  spironolactone: ['M12 3l6.5 2.2v5c0 4.2-3 7.6-6.5 8.8-3.5-1.2-6.5-4.6-6.5-8.8v-5z', 'M10 8.5v7', 'M10 12l3-3', 'M10 12l3 3'],
  eplerenone: ['M12 3l6.5 2.2v5c0 4.2-3 7.6-6.5 8.8-3.5-1.2-6.5-4.6-6.5-8.8v-5z', 'M10.2 9v6', 'M10.2 12l2.6-2.6', 'M10.2 12l2.6 2.6'],
  amiloride: ['M12 3l6.5 2.2v5c0 4.2-3 7.6-6.5 8.8-3.5-1.2-6.5-4.6-6.5-8.8v-5z', 'M10 8.5v7', 'M10 12l3-3', 'M10 12l3 3', 'M14.5 14.7h1.4'],
  triamterene: ['M12 3l6.5 2.2v5c0 4.2-3 7.6-6.5 8.8-3.5-1.2-6.5-4.6-6.5-8.8v-5z', 'M12 9v6', 'M9 12h6'],
  // Carbonic anhydrase inhibitor — droplet with a diagonal slash. In the diuretic
  // droplet family, but slashed rather than stop-barred: the thiazide bar reads as a
  // blocked TRANSPORTER, and acetazolamide blocks an ENZYME. Without an entry here it
  // fell through DRUG_ICONS to the generic equipped CHECK, which is why it was the one
  // antihypertensive rendering unlike its siblings in BOTH the map hotbar
  // (SkillTreeNodeGlyph) and the combat status rail (EffectNodeGlyph) — the two
  // surfaces share this registry, so one entry fixes both.
  acetazolamide: ['M12 4c3 4 5 6.6 5 9a5 5 0 0 1-10 0c0-2.4 2-5 5-9z', 'M8.7 16.3L15.3 9.7'],
  // Thiazides — droplet with a stop bar (blocks distal Na/Cl, mild diuresis)
  hydrochlorothiazide: ['M12 4c3 4 5 6.6 5 9a5 5 0 0 1-10 0c0-2.4 2-5 5-9z', 'M9 12.5h6'],
  chlorthalidone: ['M12 4c3 4 5 6.6 5 9a5 5 0 0 1-10 0c0-2.4 2-5 5-9z', 'M9 12.5h6', 'M9.5 15h5'],
  indapamide: ['M12 4c3 4 5 6.6 5 9a5 5 0 0 1-10 0c0-2.4 2-5 5-9z', 'M9.2 12.6h5.6', 'M12 15.2a0.8 0.8 0 1 0 0.01 0'],
  // Loop diuretics — loop of Henle (hairpin) + droplet (strong diuresis)
  furosemide: ['M8 5v6a4 4 0 0 0 8 0V5', 'M12 20c1.3-1.5 2-2.5 2-3.4a2 2 0 0 0-4 0c0 .9.7 1.9 2 3.4z'],
  bumetanide: ['M8 5v6a4 4 0 0 0 8 0V5', 'M12 19.5c1.1-1.3 1.7-2.2 1.7-3a1.7 1.7 0 0 0-3.4 0c0 .8.6 1.7 1.7 3z'],
  torsemide: ['M8 5v6a4 4 0 0 0 8 0V5', 'M12 20c1.3-1.5 2-2.5 2-3.4a2 2 0 0 0-4 0c0 .9.7 1.9 2 3.4z', 'M8 5h8'],
  // DHP CCBs — channel walls + blocked Ca (vascular calcium-channel block)
  amlodipine: ['M7.5 5v14', 'M16.5 5v14', 'M9.7 13.4h4.6', 'M12 9.4a1.4 1.4 0 1 0 0.01 0'],
  nifedipine: ['M7.5 5v14', 'M16.5 5v14', 'M9.7 12.6h4.6', 'M9.7 15h4.6', 'M12 9a1.3 1.3 0 1 0 0.01 0'],
  felodipine: ['M7.5 5v14', 'M16.5 5v14', 'M9.7 12h4.6', 'M10.7 15a0.7 0.7 0 1 0 0.01 0', 'M13.3 15a0.7 0.7 0 1 0 0.01 0'],
  // Non-DHP CCBs — channel walls + heart-rate blip (cardiac / rate control)
  verapamil: ['M7.5 5v14', 'M16.5 5v14', 'M8.8 12.4h1.6l1-2.4 1 4.8 1-2.4h1.4'],
  diltiazem: ['M7.5 5v14', 'M16.5 5v14', 'M8.8 13.2h2l1.2-2.6 1.2 2.6h2'],
  // Beta blockers — BP gauge reading LOW (dial + needle to the low zone + pivot +
  // scale ticks) + per-drug modifier. Antihypertensive-congruent: a heart / heart-
  // rate glyph reinforced the misconception that HTN is a heart-rate problem, so
  // the whole class was reworked to a "pressure reading low" gauge (Edward
  // 2026-07-02). Shared base = the first 5 paths; the trailing paths distinguish
  // the drug (β1 "1", vasodilation ticks, NO wave).
  metoprolol: ['M4 13a8 8 0 0 1 16 0', 'M12 13l-4.4-3', 'M11.05 13a0.95 0.95 0 1 0 1.9 0 0.95 0.95 0 1 0 -1.9 0', 'M5.3 10.6l1.5 0.8', 'M18.7 10.6l-1.5 0.8', 'M12 16.3v4.7', 'M10.8 17.1l1.2-0.8'],
  atenolol: ['M4 13a8 8 0 0 1 16 0', 'M12 13l-4.4-3', 'M11.05 13a0.95 0.95 0 1 0 1.9 0 0.95 0.95 0 1 0 -1.9 0', 'M5.3 10.6l1.5 0.8', 'M18.7 10.6l-1.5 0.8', 'M12 16.5v4.5'],
  bisoprolol: ['M4 13a8 8 0 0 1 16 0', 'M12 13l-4.4-3', 'M11.05 13a0.95 0.95 0 1 0 1.9 0 0.95 0.95 0 1 0 -1.9 0', 'M5.3 10.6l1.5 0.8', 'M18.7 10.6l-1.5 0.8', 'M12 16.3v4.7', 'M10.8 17.1l1.2-0.8', 'M10.7 21h2.6'],
  carvedilol: ['M4 13a8 8 0 0 1 16 0', 'M12 13l-4.4-3', 'M11.05 13a0.95 0.95 0 1 0 1.9 0 0.95 0.95 0 1 0 -1.9 0', 'M5.3 10.6l1.5 0.8', 'M18.7 10.6l-1.5 0.8', 'M6.6 18.4l-1.9 1.5', 'M17.4 18.4l1.9 1.5'],
  labetalol: ['M4 13a8 8 0 0 1 16 0', 'M12 13l-4.4-3', 'M11.05 13a0.95 0.95 0 1 0 1.9 0 0.95 0.95 0 1 0 -1.9 0', 'M5.3 10.6l1.5 0.8', 'M18.7 10.6l-1.5 0.8', 'M6.6 18.7h-2.3', 'M17.4 18.7h2.3'],
  nebivolol: ['M4 13a8 8 0 0 1 16 0', 'M12 13l-4.4-3', 'M11.05 13a0.95 0.95 0 1 0 1.9 0 0.95 0.95 0 1 0 -1.9 0', 'M5.3 10.6l1.5 0.8', 'M18.7 10.6l-1.5 0.8', 'M7.9 19.2c1.3-1.2 2.7 1.2 4 0s2.7-1.2 4 0'],
  // GI acid agents — omeprazole (PPI): acid droplet with a slash = block acid
  // production. sucralfate: a protective dome arching over a wavy mucosal surface
  // = coats/shields the ulcer. Added 2026-07-10 so their equipped-effect combat
  // chips resolve a node glyph instead of a plain-text label.
  omeprazole: ['M12 5c2.4 3.2 4 5.3 4 7.2a4 4 0 0 1-8 0c0-1.9 1.6-4 4-7.2z', 'M9 15l6-4'],
  sucralfate: ['M5 15c2-1.4 4-1.4 6 0s4 1.4 6 0', 'M6.5 12.5a5.5 5.5 0 0 1 11 0', 'M12 4v3'],
};

/* Returns an array of SVG path `d` strings for a non-keystone drug card,
   or null if the drug has no bespoke glyph. Keyed by drug_slug (lowercase). */
export function getDrugIconPaths(drugSlug) {
  if (!drugSlug) return null;
  // Normalize separators: callers pass slugs in whatever shape their source
  // uses — equipped_drugs/tree payloads arrive hyphenated ("sodium-bicarbonate")
  // while this map keys on underscores. Without this, the only multi-word
  // augment drug missed the lookup and the loadout cluster fell through to its
  // check-mark fallback (Edward 2026-06-12 — bicarbonate rendered "checked"
  // while every single-word drug matched fine).
  const slug = String(drugSlug).toLowerCase().trim().replace(/[\s-]+/g, '_');
  return DRUG_ICON_PATHS[slug] || null;
}

const KEYSTONE_BY_GENERIC = new Map(
  KEYSTONES.map((k) => [k.genericName.toLowerCase(), k]),
);
const KEYSTONE_BY_ID = new Map(KEYSTONES.map((k) => [k.id, k]));

/* Resolve a card's keystone descriptor by its generic_name (case-insensitive)
   or by an explicit keystone id. Returns null when the card isn't one of
   the three keystones. */
export function getKeystoneDescriptor(genericNameOrId) {
  if (!genericNameOrId) return null;
  const key = String(genericNameOrId).toLowerCase();
  return KEYSTONE_BY_GENERIC.get(key) || KEYSTONE_BY_ID.get(key) || null;
}

/* Returns the props PillCard needs to render a keystone gold card the
   way KeystonePickModal does — iconOverride, typeLabel, typeMode,
   accentColor. Spread onto <PillCard> at any consumer site. Returns
   undefined when the card is not a keystone (so the spread is a no-op). */
export function getKeystonePillCardProps(genericNameOrId) {
  const k = getKeystoneDescriptor(genericNameOrId);
  if (!k) return undefined;
  return {
    iconOverride: k.icon,
    typeLabel: 'Keystone',
    typeMode: 'gold',
    accentColor: k.accent,
  };
}
